/** Corner snake that hunts the nearest fighter during pace-off and aim. */

import { ARENA_H, ARENA_W, type Role } from "@duelgame/shared";

export const SNAKE_FRAMES = 4;
/** Fallback frame size before the sheet loads. */
export const SNAKE_FRAME_W = 48;
export const SNAKE_FRAME_H = 24;
const SNAKE_CHANCE = 0.15;
/** Slow crawl toward the nearest player (px/s). */
const SPEED = 38;
const HIT_RADIUS = 18;
const CORNER_INSET = 36;
/** Seconds per walk-cycle frame. */
const FRAME_MS = 140;

export interface SnakeState {
  x: number;
  y: number;
  /** Radians; 0 faces right (spritesheet direction). */
  angle: number;
  /** Animation clock (ms). */
  animMs: number;
  alive: boolean;
}

const CORNERS: Array<{ x: number; y: number }> = [
  { x: CORNER_INSET, y: CORNER_INSET },
  { x: ARENA_W - CORNER_INSET, y: CORNER_INSET },
  { x: CORNER_INSET, y: ARENA_H - CORNER_INSET },
  { x: ARENA_W - CORNER_INSET, y: ARENA_H - CORNER_INSET },
];

export function snakeFrame(s: SnakeState): number {
  return Math.floor(s.animMs / FRAME_MS) % SNAKE_FRAMES;
}

export function maybeRollSnake(seed: number): number | null {
  let a = (seed ^ 0x51c3a91b) >>> 0;
  const next = () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  if (next() >= SNAKE_CHANCE) return null;
  return Math.floor(next() * CORNERS.length);
}

export class SnakeField {
  snake: SnakeState | null = null;

  clear(): void {
    this.snake = null;
  }

  /** Remove the snake (shot dead). */
  kill(): void {
    this.snake = null;
  }

  /** Spawn in a corner, or clear when corner is null. */
  spawn(cornerIndex: number | null): void {
    if (cornerIndex === null) {
      this.snake = null;
      return;
    }
    const c = CORNERS[cornerIndex % CORNERS.length]!;
    // Point roughly toward arena center from the spawn corner.
    const angle = Math.atan2(ARENA_H / 2 - c.y, ARENA_W / 2 - c.x);
    this.snake = {
      x: c.x,
      y: c.y,
      angle,
      animMs: 0,
      alive: true,
    };
  }

  snapshot(): SnakeState | null {
    return this.snake ? { ...this.snake } : null;
  }

  applyRemote(s: SnakeState | null | undefined): void {
    if (!s) {
      this.snake = null;
      return;
    }
    if (!this.snake) {
      this.snake = { ...s };
      return;
    }
    this.snake.x = s.x;
    this.snake.y = s.y;
    this.snake.angle = s.angle;
    this.snake.animMs = s.animMs;
    this.snake.alive = s.alive;
  }

  /**
   * Crawl toward the nearest living fighter. Returns which role was bitten,
   * if any.
   */
  update(
    dt: number,
    host: { x: number; y: number; hit: boolean },
    guest: { x: number; y: number; hit: boolean },
    hunting: boolean
  ): Role | null {
    const s = this.snake;
    if (!s || !s.alive) return null;

    if (hunting) {
      s.animMs += dt * 1000;
      let target: { x: number; y: number; role: Role } | null = null;
      const candidates: Array<{ x: number; y: number; role: Role }> = [];
      if (!host.hit) candidates.push({ x: host.x, y: host.y, role: "host" });
      if (!guest.hit) candidates.push({ x: guest.x, y: guest.y, role: "guest" });
      let best = Infinity;
      for (const c of candidates) {
        const d = Math.hypot(c.x - s.x, c.y - s.y);
        if (d < best) {
          best = d;
          target = c;
        }
      }
      if (target) {
        const dx = target.x - s.x;
        const dy = target.y - s.y;
        const dist = Math.hypot(dx, dy) || 1;
        const step = SPEED * dt;
        if (dist <= step) {
          s.x = target.x;
          s.y = target.y;
        } else {
          s.x += (dx / dist) * step;
          s.y += (dy / dist) * step;
        }
        s.angle = Math.atan2(dy, dx);
      }
    }

    // Bite check (even if not hunting this frame — still touching).
    for (const f of [
      { role: "host" as const, ...host },
      { role: "guest" as const, ...guest },
    ]) {
      if (f.hit) continue;
      if (Math.hypot(f.x - s.x, f.y - s.y) <= HIT_RADIUS) {
        return f.role;
      }
    }
    return null;
  }
}
